import type { CategoryItem } from '../lib/catalog-client';

interface CategoryChipsProps {
  /** Head categories to render (already sorted + sliced). */
  categories: CategoryItem[];
  /** Controlled selection. */
  value: CategoryItem[];
  /** Pro users multi-select; Free users are radio (single). */
  isPro: boolean;
  /** Max categories selectable right now (accounts for niches picked via search). */
  max: number;
  onChange: (value: CategoryItem[]) => void;
}

/**
 * Onboarding category picker — the discoverable "head" of the taxonomy. Renders
 * the top categories as tappable chips. Free = radio (one total); Pro = multi up
 * to `max`. The long tail is reached via the ИКПУ search fallback, not here.
 */
export function CategoryChips({ categories, value, isPro, max, onChange }: CategoryChipsProps) {
  const selected = new Set(value.map((v) => v.slug));

  function toggle(cat: CategoryItem) {
    if (selected.has(cat.slug)) {
      onChange(value.filter((v) => v.slug !== cat.slug));
      return;
    }
    if (!isPro) {
      onChange([cat]); // radio: replace whatever was picked
      return;
    }
    if (value.length >= max) return; // Pro cap reached
    onChange([...value, cat]);
  }

  // A dense pill-wrap grid: chips wrap across the row so the ~10 quick-pick
  // categories fit without a tall scroll, while min-h keeps each a comfortable
  // mobile tap target. Categories beyond these are reached via the search box.
  return (
    <div className="flex flex-wrap gap-1.5">
      {categories.map((cat) => {
        const isSel = selected.has(cat.slug);
        const atCap = isPro && !isSel && value.length >= max;
        return (
          <button
            key={cat.slug}
            type="button"
            aria-pressed={isSel}
            disabled={atCap}
            onClick={() => toggle(cat)}
            className={
              isSel
                ? 'inline-flex min-h-[36px] items-center rounded-full border-[1.5px] border-brand bg-brand/10 px-3 py-1 text-[12.5px] font-semibold leading-tight text-brand'
                : 'inline-flex min-h-[36px] items-center rounded-full border border-edge bg-bone-surface px-3 py-1 text-[12.5px] leading-tight text-ink-soft transition-colors hover:border-brand/40 disabled:cursor-not-allowed disabled:opacity-40'
            }
          >
            {cat.name}
          </button>
        );
      })}
    </div>
  );
}
